// src/orders/OrdersView.js
import React, { useState, useEffect } from 'react';
import { callApi } from '../api/api'; // Import callApi
import OrderTimeline from './OrderTimeline';
import DriverMapRoute from '../components/DriverMapRoute';

const OrdersView = ({ isAuthenticated }) => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchOrders = async (silent = false) => {
            if (!isAuthenticated) {
                setError('Please log in to view your orders.');
                setLoading(false);
                return;
            }
            if (!silent) setLoading(true);
            try {
                const fetchedOrders = await callApi('/orders', 'GET');
                setOrders(fetchedOrders || []);
            } catch (err) {
                console.error('Error fetching orders:', err);
                if (!silent) setError('Failed to load orders. Please ensure the backend server is running.');
            } finally {
                if (!silent) setLoading(false);
            }
        };

        fetchOrders();

        // Polling interval
        const intervalId = setInterval(() => {
            fetchOrders(true);
        }, 10000);

        return () => clearInterval(intervalId);
    }, [isAuthenticated]);

    // Hardcoded Store Location
    const storeLocation = { lat: 19.0760, lng: 72.8777 }; // Example: Mumbai

    return (
        <section id="orders" className="page bg-white p-8 rounded-lg shadow-xl">
            <h2 className="text-4xl font-bold text-center text-teal-700 mb-8">Your Orders</h2>
            <p className="text-center text-gray-700 text-lg mb-6">View the status and details of all your past and current orders. Each order card provides a summary of your purchase.</p>
            <div id="order-list" className="space-y-6">
                {loading && <p className="text-center text-gray-600 text-lg">Loading orders...</p>}
                {error && <p className="text-center text-red-600 text-lg">{error}</p>}
                {!loading && !error && orders.length === 0 && (
                    <p className="text-center text-gray-600 text-lg">You have no past orders.</p>
                )}
                {!loading && !error && orders.length > 0 && (
                    orders.map(order => (
                        <div key={order._id} className="bg-gray-50 p-6 rounded-lg shadow-md mb-4">
                            <div className="flex justify-between items-center mb-4">
                                <div>
                                    <h3 className="text-2xl font-bold text-teal-700">Order ID: {order.orderId}</h3>
                                    <span className={`inline-block mt-1 px-3 py-1 rounded-full text-xs font-bold ${
                                        order.status === 'Pending' ? 'bg-amber-100 text-amber-700' :
                                        order.status === 'Payment Verification Pending' ? 'bg-purple-100 text-purple-700' :
                                        order.status === 'Processing' ? 'bg-sky-100 text-sky-700' :
                                        order.status === 'Awaiting Bill' ? 'bg-fuchsia-100 text-fuchsia-700' :
                                        'bg-slate-100 text-slate-700'
                                    }`}>{order.status}</span>
                                </div>
                                {order.status === 'Pending' && (
                                    <a 
                                        href={`/#pay/${order.orderId}`}
                                        className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-1.5 px-4 rounded-lg transition-colors shadow-sm flex items-center gap-1.5 text-sm"
                                    >
                                        <i className="fas fa-credit-card text-xs"></i> Pay Now
                                    </a>
                                )}
                            </div>
                            
                            {(order.status === 'Out for Delivery' || order.status === 'Shipped') && (
                                <div className="mb-6">
                                    <h4 className="text-xl font-bold text-sky-700 mb-2 flex items-center gap-2"><i className="fas fa-map-marked-alt"></i> Live Tracking</h4>
                                    <DriverMapRoute 
                                        storeLocation={storeLocation}
                                        customerAddress={order.deliveryAddress}
                                        driverLocation={order.driverLocation}
                                    />
                                </div>
                            )}

                            <p className="text-gray-700 mb-2">Order Date: {new Date(order.orderDate).toLocaleString('en-IN')}</p>
                            <p className="text-gray-700 mb-4">Total: <span className="font-bold text-emerald-700">₹{order.total.toFixed(2)}</span></p>
                            
                            <div className="mb-6">
                                <OrderTimeline currentStatus={order.status} />
                            </div>

                            <div className="border-t border-gray-200 pt-4">
                                <h4 className="text-xl font-semibold text-gray-800 mb-2">Items:</h4>
                                <ul className="list-disc list-inside space-y-1 text-gray-600">
                                    {order.items.map((item, idx) => (
                                        <li key={idx}>{item.name} (x{item.quantity}) - ₹{(item.price * item.quantity).toFixed(2)}</li>
                                    ))}
                                </ul>
                            </div>
                            
                            {(order.prescriptionUrls && order.prescriptionUrls.length > 0) ? (
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                    <h4 className="text-sm font-semibold text-gray-800 mb-2">Prescription/List Images:</h4>
                                    <div className="flex flex-wrap gap-2">
                                        {order.prescriptionUrls.map((url, idx) => (
                                            <a key={idx} href={url} target="_blank" rel="noreferrer">
                                                <img src={url} alt={`Prescription ${idx + 1}`} className="w-24 h-24 object-cover rounded-lg border border-gray-300 hover:opacity-80 transition-opacity" />
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            ) : order.prescriptionUrl && (
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                    <h4 className="text-sm font-semibold text-gray-800 mb-2">Prescription:</h4>
                                    <a href={order.prescriptionUrl} target="_blank" rel="noreferrer">
                                        <img src={order.prescriptionUrl} alt="Prescription" className="w-24 h-24 object-cover rounded-lg border border-gray-300 hover:opacity-80 transition-opacity" />
                                    </a>
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>
        </section>
    );
};

export default OrdersView;
